import { Hours } from '@obraplan/core/domain/shared/hours';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { cn } from '@/lib/cn';

/**
 * Gráficos do painel.
 *
 * Desenhados aqui, em SVG e CSS — **nenhuma biblioteca, nenhum recurso de CDN** (RN-138).
 * O sistema roda sem internet, e um gráfico que só aparece quando o canteiro tem sinal é
 * um gráfico que não existe.
 *
 * Três regras que valem para todos:
 *
 * 1. **Cor nunca é o único indicador.** Toda série tem legenda escrita, todo valor é
 *    rotulado, e quem imprime em preto e branco lê a mesma coisa. Nas barras sobrepostas
 *    a espessura também distingue: disponível é a faixa grossa, planejado a média,
 *    realizado a fina — e a legenda repete as três espessuras.
 * 2. **Toda figura tem a tabela por baixo.** O `<details>` "ver como tabela" é a
 *    alternativa textual do leitor de tela e do relatório impresso — não é um extra.
 * 3. **O componente não calcula nada.** Soma, desvio e saldo vêm prontos do domínio.
 */

export type SeriesTone = 'planned' | 'executed' | 'available' | 'attention';

const SERIES_FILL: Readonly<Record<SeriesTone, string>> = {
  available: 'var(--color-state-neutral)',
  planned: 'var(--color-brand)',
  executed: 'var(--color-state-ok)',
  attention: 'var(--color-state-warn)',
};

/** Espessura de cada série nas barras sobrepostas — o segundo sinal, além da cor. */
const SERIES_THICKNESS: Readonly<Record<SeriesTone, string>> = {
  available: 'h-2.5',
  planned: 'h-1.5',
  executed: 'h-[3px]',
  attention: 'h-1.5',
};

/** Hachura para quem não distingue as cores — e para o papel. */
const SERIES_PATTERN: Readonly<Record<SeriesTone, string | undefined>> = {
  available: undefined,
  planned: undefined,
  executed: 'url(#hatch-executed)',
  attention: undefined,
};

export interface ChartSeries {
  readonly key: string;
  readonly label: string;
  readonly tone: SeriesTone;
}

export interface ChartPoint {
  readonly key: string;
  readonly label: string;
  /** Um valor por série, na mesma ordem de `series`. */
  readonly values: readonly number[];
}

function formatHours(quarters: number): string {
  return Hours.ofQuarters(Math.round(quarters)).format();
}

export function ChartLegend({
  series,
  className,
}: {
  series: readonly ChartSeries[];
  className?: string;
}) {
  return (
    <ul className={cn('flex flex-wrap items-center gap-x-4 gap-y-1 text-xs', className)}>
      {series.map((item) => (
        <li key={item.key} className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="size-2.5 shrink-0 rounded-[2px]"
            style={{ backgroundColor: SERIES_FILL[item.tone] }}
          />
          <span className="text-(--color-content-muted)">{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

function ChartTable({
  caption,
  series,
  points,
  format,
}: {
  caption: string;
  series: readonly ChartSeries[];
  points: readonly ChartPoint[];
  format: (value: number) => string;
}) {
  return (
    <details className="mt-3 text-sm">
      <summary className="cursor-pointer text-xs text-(--color-content-muted)">
        Ver como tabela
      </summary>
      <TableWrapper className="mt-2">
        <Table>
          <caption className="sr-only">{caption}</caption>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              {series.map((item) => (
                <TableHead key={item.key}>{item.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {points.map((point) => (
              <TableRow key={point.key}>
                <TableCell className="font-medium">{point.label}</TableCell>
                {series.map((item, index) => (
                  <TableCell key={item.key} className="tabular">
                    {format(point.values[index] ?? 0)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableWrapper>
    </details>
  );
}

export interface TimeSeriesBarsProps {
  title: string;
  series: readonly ChartSeries[];
  points: readonly ChartPoint[];
  /** Como escrever um valor. Horas por padrão; a série de alterações usa contagem. */
  format?: (value: number) => string;
  className?: string;
}

/**
 * Barras agrupadas ao longo do tempo.
 *
 * O eixo Y não tem números: quem precisa do número exato abre a tabela, e o topo da
 * escala está escrito no canto. Um eixo com escala falsa — que não começa no zero —
 * mentiria sobre a proporção, então este sempre começa no zero.
 *
 * Só as barras vivem no SVG, que estica para caber na largura; os rótulos ficam em HTML,
 * para não esticarem junto.
 */
export function TimeSeriesBars({
  title,
  series,
  points,
  format = formatHours,
  className,
}: TimeSeriesBarsProps) {
  const max = points.reduce(
    (highest, point) => Math.max(highest, ...point.values.map((value) => value)),
    0,
  );

  if (points.length === 0) {
    return (
      <p className={cn('text-sm text-(--color-content-muted)', className)}>
        Nada no período escolhido.
      </p>
    );
  }

  const width = Math.max(points.length * 56, 320);
  const height = 150;
  const groupWidth = width / points.length;
  const barWidth = Math.min(16, (groupWidth - 10) / series.length);

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ChartLegend series={series} />
        <span className="tabular text-[11px] text-(--color-content-muted)">
          escala até {format(max)}
        </span>
      </div>
      <div className="mt-2 overflow-x-auto">
        <div className="min-w-80">
          <svg
            viewBox={`0 0 ${String(width)} ${String(height)}`}
            className="h-36 w-full"
            preserveAspectRatio="none"
            role="img"
            aria-label={`${title}. ${String(points.length)} períodos. Os valores exatos estão na tabela abaixo do gráfico.`}
          >
            <defs>
              <pattern
                id="hatch-executed"
                width="4"
                height="4"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <rect width="4" height="4" fill={SERIES_FILL.executed} />
                <line x1="0" y1="0" x2="0" y2="4" stroke="var(--color-surface)" strokeWidth="1.2" />
              </pattern>
            </defs>

            <line
              x1="0"
              y1={height - 0.5}
              x2={width}
              y2={height - 0.5}
              stroke="var(--color-border-subtle)"
              strokeWidth="1"
            />

            {points.map((point, index) => {
              const groupCenter = index * groupWidth + groupWidth / 2;
              const seriesWidth = barWidth * series.length + 2 * (series.length - 1);

              return (
                <g key={point.key}>
                  {series.map((item, seriesIndex) => {
                    const value = point.values[seriesIndex] ?? 0;
                    const barHeight = max === 0 ? 0 : (value / max) * (height - 8);
                    const x = groupCenter - seriesWidth / 2 + seriesIndex * (barWidth + 2);
                    return (
                      <rect
                        key={item.key}
                        x={x}
                        y={height - 1 - barHeight}
                        width={barWidth}
                        height={Math.max(barHeight, value > 0 ? 1.5 : 0)}
                        fill={SERIES_PATTERN[item.tone] ?? SERIES_FILL[item.tone]}
                      />
                    );
                  })}
                </g>
              );
            })}
          </svg>
          <div
            className="mt-1.5 grid text-center text-[11px] tabular text-(--color-content-muted)"
            style={{ gridTemplateColumns: `repeat(${String(points.length)}, minmax(0, 1fr))` }}
            aria-hidden="true"
          >
            {points.map((point) => (
              <span key={point.key} className="truncate">
                {point.label}
              </span>
            ))}
          </div>
        </div>
      </div>
      <ChartTable caption={title} series={series} points={points} format={format} />
    </div>
  );
}

export interface ComparisonRow {
  readonly key: string;
  readonly label: string;
  readonly values: readonly number[];
  /** Texto curto sob o rótulo — desvio, saldo, "3 dias por conferir". */
  readonly note?: string | undefined;
  readonly noteTone?: 'muted' | 'ok' | 'warn' | 'danger';
}

const NOTE_TONE: Readonly<Record<NonNullable<ComparisonRow['noteTone']>, string>> = {
  muted: 'text-(--color-content-muted)',
  ok: 'text-(--color-state-ok)',
  warn: 'text-(--color-state-warn)',
  danger: 'text-(--color-state-danger)',
};

export interface ComparisonBarsProps {
  title: string;
  series: readonly ChartSeries[];
  rows: readonly ComparisonRow[];
  format?: (value: number) => string;
  emptyMessage?: string;
  className?: string;
}

/**
 * Barras horizontais sobrepostas, uma linha por item.
 *
 * Formato escolhido para o recorte com nome longo — disciplina, pavimento, pessoa. Barra
 * vertical com "Impermeabilização" embaixo vira texto girado que ninguém lê no tablet.
 *
 * As séries se sobrepõem numa faixa só, da mais grossa à mais fina, em vez de uma faixa
 * por série: três faixas por pessoa faziam vinte pessoas ocuparem duas telas. Os números
 * ficam em colunas alinhadas à direita, com a legenda por cabeçalho — assim se lê de
 * cima a baixo como uma tabela, e se compara de lado como um gráfico.
 */
export function ComparisonBars({
  title,
  series,
  rows,
  format = formatHours,
  emptyMessage = 'Nada no período escolhido.',
  className,
}: ComparisonBarsProps) {
  const max = rows.reduce((highest, row) => Math.max(highest, ...row.values), 0);

  if (rows.length === 0) {
    return <p className={cn('text-sm text-(--color-content-muted)', className)}>{emptyMessage}</p>;
  }

  const columns = `minmax(0, 1fr) repeat(${String(series.length)}, 4.5rem)`;

  return (
    <div className={className}>
      {/* A legenda é o cabeçalho das colunas de número. */}
      <div className="grid items-end gap-x-2" style={{ gridTemplateColumns: columns }}>
        <span className="sr-only">Item</span>
        <span aria-hidden="true" />
        {series.map((item) => (
          <span
            key={item.key}
            className="flex items-center justify-end gap-1 text-[11px] leading-tight text-(--color-content-muted)"
          >
            <span
              aria-hidden="true"
              className={cn('w-3 shrink-0 rounded-[1px]', SERIES_THICKNESS[item.tone])}
              style={{ backgroundColor: SERIES_FILL[item.tone] }}
            />
            <span className="truncate">{item.label}</span>
          </span>
        ))}
      </div>

      <ul className="mt-2 flex flex-col gap-2.5">
        {rows.map((row) => (
          <li
            key={row.key}
            className="grid gap-x-2 gap-y-1"
            style={{ gridTemplateColumns: columns }}
          >
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium leading-tight">{row.label}</span>
              {row.note !== undefined && (
                <span
                  className={cn('block text-xs leading-tight', NOTE_TONE[row.noteTone ?? 'muted'])}
                >
                  {row.note}
                </span>
              )}
            </span>
            {series.map((item, index) => (
              <span
                key={item.key}
                className="self-start text-right tabular text-sm leading-tight text-(--color-content)"
              >
                {format(row.values[index] ?? 0)}
              </span>
            ))}

            <div
              className="relative h-2.5 overflow-hidden rounded-sm bg-(--color-surface-muted)"
              style={{ gridColumn: '1 / -1' }}
            >
              {series.map((item, index) => {
                const value = row.values[index] ?? 0;
                const ratio = max === 0 ? 0 : value / max;
                return (
                  <div
                    key={item.key}
                    className={cn(
                      'absolute left-0 top-1/2 -translate-y-1/2 rounded-sm',
                      SERIES_THICKNESS[item.tone],
                    )}
                    style={{
                      width: `${String(Math.max(ratio * 100, value > 0 ? 0.75 : 0))}%`,
                      backgroundColor: SERIES_FILL[item.tone],
                    }}
                    role="meter"
                    aria-valuenow={value}
                    aria-valuemin={0}
                    aria-valuemax={max}
                    aria-label={`${row.label} — ${item.label}: ${format(value)}`}
                  />
                );
              })}
            </div>
          </li>
        ))}
      </ul>
      <ChartTable
        caption={title}
        series={series}
        points={rows.map((row) => ({ key: row.key, label: row.label, values: row.values }))}
        format={format}
      />
    </div>
  );
}
